import {
  DayOfWeek,
  KitchenThrottleConfig,
  KitchenThrottleValues,
} from 'api/ordering-service/ordering-service.types';
import { Option } from 'util/multi-select';
import i18n from 'app-i18n';
import { DateTime, Interval } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { blue, green } from '@mui/material/colors';
import { EventApi, EventInput } from '@fullcalendar/core/index.js';
import { DUMMY_EVENT_ID, dayOfWeekMap } from '../kitchen-capacity.utils';
import { daysOfWeekOptions } from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.utils';

export type OrderLimitsType = 'VALUE' | 'VOLUME';

export type ExtendedPropsType = {
  orderLimitsType: OrderLimitsType;
  eventType: 'OrderLimits';
  kitchenThrottleValues: KitchenThrottleValues;
};

export type SetOrderLimitsDrawerDataProps = {
  id?: string;
  title: string;
  start: DateTime;
  end: DateTime;
  kitchenThrottleValues: KitchenThrottleValues;
};

export type SetOrderLimitsFormDataProps = {
  eventId?: string;
  daysOfWeek?: Option[];
  dateRange?: string[];
  timeRange?: string[];
  orderLimitsType?: OrderLimitsType[];
  kitchenThrottleValue?: number;
  kitchenThrottleVolume?: number;
};

const getTimeFromSlotIndex = (
  index: number,
): { startTime: string; endTime: string } => {
  const minutes = index * 15;
  const startTime = new Date(0, 0, 0, 0, minutes).toTimeString().split(' ')[0];
  const endTime = new Date(0, 0, 0, 0, minutes + 15)
    .toTimeString()
    .split(' ')[0];
  return { startTime, endTime };
};

export const convertKitchenThrottleSettingsToEventInput = (
  data: KitchenThrottleConfig,
): EventInput[] => {
  const events = [];

  if (!data || !data.timeslots) {
    return events;
  }

  for (const day in data.timeslots) {
    const daySlots = data.timeslots[day];
    const dayIndex = parseInt(dayOfWeekMap[day]);

    let currentVolumeSlot: any = null;
    let currentValueSlot: any = null;

    daySlots.forEach((slot: any, index: number) => {
      const { startTime, endTime } = getTimeFromSlotIndex(index);

      // Handle volume events (count)
      if (slot.count !== null && slot.throttlingEnabled) {
        if (!currentVolumeSlot) {
          // Start a new volume slot range
          currentVolumeSlot = {
            startTime,
            endTime,
            count: slot.count,
          };
        } else if (currentVolumeSlot.count === slot.count) {
          // Extend the existing volume slot
          currentVolumeSlot.endTime = endTime;
        } else {
          // Push the previous volume event and start a new one
          events.push({
            id: uuidv4(),
            title: i18n.t('KitchenCapacity.eventTitles.orderLimits.volume'),
            daysOfWeek: [dayIndex],
            startTime: currentVolumeSlot.startTime,
            endTime: currentVolumeSlot.endTime,
            extendedProps: {
              orderLimitsType: 'VOLUME',
              eventType: 'OrderLimits',
              kitchenThrottleValues: {
                count: currentVolumeSlot.count,
                value: null,
                throttlingEnabled: true,
              },
            },
            color: green[500],
          });
          currentVolumeSlot = {
            startTime,
            endTime,
            count: slot.count,
          };
        }
      } else if (currentVolumeSlot) {
        // Push the current volume event if it ends
        events.push({
          id: uuidv4(),
          title: i18n.t('KitchenCapacity.eventTitles.orderLimits.volume'),
          daysOfWeek: [dayIndex],
          startTime: currentVolumeSlot.startTime,
          endTime: currentVolumeSlot.endTime,
          extendedProps: {
            orderLimitsType: 'VOLUME',
            eventType: 'OrderLimits',
            kitchenThrottleValues: {
              count: currentVolumeSlot.count,
              value: null,
              throttlingEnabled: true,
            },
          },
          color: green[500],
        });
        currentVolumeSlot = null;
      }

      // Handle value events (currency)
      if (slot.value !== null && slot.throttlingEnabled) {
        if (!currentValueSlot) {
          // Start a new value slot range
          currentValueSlot = {
            startTime,
            endTime,
            value: slot.value / 1000, // convert currency
          };
        } else if (currentValueSlot.value === slot.value / 1000) {
          // Extend the existing value slot
          currentValueSlot.endTime = endTime;
        } else {
          // Push the previous value event and start a new one
          events.push({
            id: uuidv4(),
            title: i18n.t('KitchenCapacity.eventTitles.orderLimits.value'),
            daysOfWeek: [dayIndex],
            startTime: currentValueSlot.startTime,
            endTime: currentValueSlot.endTime,
            extendedProps: {
              orderLimitsType: 'VALUE',
              eventType: 'OrderLimits',
              kitchenThrottleValues: {
                count: null,
                value: currentValueSlot.value,
                throttlingEnabled: true,
              },
            },
            color: blue[500],
          });
          currentValueSlot = {
            startTime,
            endTime,
            value: slot.value / 1000,
          };
        }
      } else if (currentValueSlot) {
        // Push the current value event if it ends
        events.push({
          id: uuidv4(),
          title: i18n.t('KitchenCapacity.eventTitles.orderLimits.value'),
          daysOfWeek: [dayIndex],
          startTime: currentValueSlot.startTime,
          endTime: currentValueSlot.endTime,
          extendedProps: {
            orderLimitsType: 'VALUE',
            eventType: 'OrderLimits',
            kitchenThrottleValues: {
              count: null,
              value: currentValueSlot.value,
              throttlingEnabled: true,
            },
          },
          color: blue[500],
        });
        currentValueSlot = null;
      }
    });

    // If there are still ongoing volume or value slots at the end of the day, push them
    if (currentVolumeSlot) {
      events.push({
        id: uuidv4(),
        title: i18n.t('KitchenCapacity.eventTitles.orderLimits.volume'),
        daysOfWeek: [dayIndex],
        startTime: currentVolumeSlot.startTime,
        endTime: currentVolumeSlot.endTime,
        extendedProps: {
          orderLimitsType: 'VOLUME',
          eventType: 'OrderLimits',
          kitchenThrottleValues: {
            count: currentVolumeSlot.count,
            value: null,
            throttlingEnabled: true,
          },
        },
        color: green[500],
      });
    }

    if (currentValueSlot) {
      events.push({
        id: uuidv4(),
        title: i18n.t('KitchenCapacity.eventTitles.orderLimits.value'),
        daysOfWeek: [dayIndex],
        startTime: currentValueSlot.startTime,
        endTime: currentValueSlot.endTime,
        extendedProps: {
          orderLimitsType: 'VALUE',
          eventType: 'OrderLimits',
          kitchenThrottleValues: {
            count: null,
            value: currentValueSlot.value,
            throttlingEnabled: true,
          },
        },
        color: blue[500],
      });
    }
  }

  return events;
};

export const convertEventApiToRecurring = (event: EventApi): EventInput => {
  const startDateTime = DateTime.fromJSDate(event.start);
  const endDateTime = DateTime.fromJSDate(event.end);

  return {
    id: event.id,
    title: event.title,
    daysOfWeek: [(startDateTime.weekday % 7).toString()],
    startTime: startDateTime.toFormat('HH:mm:ss'),
    endTime: endDateTime.toFormat('HH:mm:ss'),
    extendedProps: event.extendedProps,
    color: event.backgroundColor,
  };
};

const getSlotIndexFromTime = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours * 60 + minutes) / 15;
};

export const recurringEventsToKitchenThrottleConfig = (events: any[] = []) => {
  const filteredEvents = events?.filter(
    event => event.extendedProps.eventType !== 'LocationHours',
  );

  const timeslots: Record<string, any[]> = {};

  if (!filteredEvents || filteredEvents.length === 0) {
    return timeslots;
  }

  Object.keys(dayOfWeekMap).forEach(key => {
    timeslots[key] = Array(96).fill({
      count: null,
      value: null,
      throttlingEnabled: false,
    });
  });

  filteredEvents?.forEach((event: any) => {
    const dayOfWeek = event.daysOfWeek[0];
    const dayName = Object.keys(dayOfWeekMap).find(
      key => dayOfWeekMap[key] === String(dayOfWeek),
    ) as DayOfWeek;

    const startTime = event.startTime;
    const endTime = event.endTime;

    const startSlotIndex = getSlotIndexFromTime(startTime);
    const endSlotIndex = getSlotIndexFromTime(endTime);

    // Loop through the range of indices from start to end
    for (let i = startSlotIndex; i < endSlotIndex; i++) {
      if (event.title === 'Value') {
        timeslots[dayName][i] = {
          ...timeslots[dayName][i],
          value: event.extendedProps.kitchenThrottleValues.value * 1000, // reTransform currency
          throttlingEnabled:
            event.extendedProps.kitchenThrottleValues.throttlingEnabled,
        };
      } else {
        // event.title = Volume case
        timeslots[dayName][i] = {
          ...timeslots[dayName][i],
          count: event.extendedProps.kitchenThrottleValues.count,
          throttlingEnabled:
            event.extendedProps.kitchenThrottleValues.throttlingEnabled,
        };
      }
    }
  });

  return timeslots;
};

export const convertSetOrderLimitsFormDataToEventSource = (
  data: SetOrderLimitsFormDataProps,
): EventInput[] => {
  const {
    daysOfWeek,
    timeRange,
    orderLimitsType,
    kitchenThrottleValue,
    kitchenThrottleVolume,
  } = data;
  const startTime = DateTime.fromISO(timeRange[0]).toFormat('HH:mm:ss');
  const endTime = DateTime.fromISO(timeRange[1]).toFormat('HH:mm:ss');

  return daysOfWeek.flatMap(day => {
    return orderLimitsType.map(type => {
      return {
        id: uuidv4(),
        title:
          type === 'VALUE'
            ? i18n.t('KitchenCapacity.eventTitles.orderLimits.value')
            : i18n.t('KitchenCapacity.eventTitles.orderLimits.volume'),
        daysOfWeek: [dayOfWeekMap[day.label.toUpperCase()]],
        startTime,
        endTime,
        extendedProps: {
          orderLimitsType: type,
          eventType: 'OrderLimits',
          kitchenThrottleValues: {
            count: kitchenThrottleVolume,
            value: kitchenThrottleValue,
            throttlingEnabled: true,
          },
        },
        color: type === 'VALUE' ? blue[500] : green[500],
      };
    });
  });
};

export const filterOrderLimitsOverlappedEvents = (
  data: SetOrderLimitsFormDataProps,
  existingEvents: EventInput[],
): EventInput[] => {
  const { eventId, daysOfWeek, timeRange, orderLimitsType } = data;

  if (!timeRange.every(time => DateTime.fromISO(time).isValid)) {
    return [];
  }

  const [startTime, endTime] = timeRange.map(time => {
    const { hour, minute, second, millisecond } = DateTime.fromISO(time);
    return DateTime.fromObject({ hour, minute, second, millisecond });
  });

  const inputInterval = Interval.fromDateTimes(startTime, endTime);

  // Filter events from eventArray based on matching day of week and order type
  return existingEvents.filter(event => {
    const eventStartTime = DateTime.fromFormat(event.startTime, 'HH:mm:ss');
    const eventEndTime = DateTime.fromFormat(event.endTime, 'HH:mm:ss');

    return (
      ![eventId, DUMMY_EVENT_ID].includes(event.id) &&
      event.extendedProps.eventType !== 'LocationHours' &&
      orderLimitsType.includes(event.extendedProps.orderLimitsType) &&
      (daysOfWeek[0]?.id === 'ALL' ||
        daysOfWeek.some(
          day =>
            day.label.toUpperCase() ===
            Object.keys(dayOfWeekMap)[event.daysOfWeek[0]],
        )) &&
      DateTime.fromISO(event.startTime) <= DateTime.fromISO(event.endTime) &&
      Interval.fromDateTimes(eventStartTime, eventEndTime).overlaps(
        inputInterval,
      )
    );
  });
};

export const createOrderLimitsDummyEvent = (
  start: DateTime,
  end: DateTime,
): EventInput => {
  return {
    id: DUMMY_EVENT_ID,
    title: i18n.t('KitchenCapacity.eventTitles.orderLimits.value'),
    start: start.toJSDate(),
    end: end.toJSDate(),
    extendedProps: {
      orderLimitsType: 'VALUE',
      eventType: 'OrderLimits',
      kitchenThrottleValues: {
        count: 1,
        value: 1,
        throttlingEnabled: true,
      },
    },
    color: blue[500],
  };
};

export const convertToSetOrderLimitsFormData = ({
  id,
  title,
  start,
  end,
  kitchenThrottleValues,
}: SetOrderLimitsDrawerDataProps): SetOrderLimitsFormDataProps => {
  let orderLimitsType: OrderLimitsType[] = [];

  const dayOfWeek = start?.setLocale('en-US').weekdayShort?.toUpperCase();

  const daysOfWeek = [
    {
      id: dayOfWeek,
      label: daysOfWeekOptions().find(option => option.id === dayOfWeek)?.label,
    },
  ];

  const dateRange = [start.startOf('day').toISO(), end.endOf('day').toISO()];

  const timeRange = [start.toISO(), end.toISO()];

  switch (title) {
    case i18n.t('KitchenCapacity.eventTitles.orderLimits.value'):
      orderLimitsType = ['VALUE'];
      break;
    case i18n.t('KitchenCapacity.eventTitles.orderLimits.volume'):
      orderLimitsType = ['VOLUME'];
      break;
  }

  const kitchenThrottleValue = kitchenThrottleValues.value;
  const kitchenThrottleVolume = kitchenThrottleValues.count;

  return {
    eventId: id ?? null,
    orderLimitsType,
    daysOfWeek,
    dateRange,
    timeRange,
    kitchenThrottleValue,
    kitchenThrottleVolume,
  };
};
